import React from 'react'
import { Button, DatePicker, Form, Input, Modal, Select, Tag, TimePicker } from 'antd'
import { FormComponentProps } from 'antd/lib/form'
import moment from 'moment'
import { COMMISSION_SELECT_STYLES } from '@pages/Clients/Agent/Commissions/CreateNewCommission/commissionSelectStyles'
import NotificationEmojiPicker from './NotificationEmojiPicker'
import { SectionTitle, StepBar, renderWizardFooter, makeWizardHandlers } from '../shared/mediwhoAppStyles'
import { parseTargets, makeTargetTypeHandler, TargetChipField } from '../shared/ChipInput'
import { ACTION_OPTIONS } from './notificationConstants'

type TargetType = 'all' | 'hmo' | 'user_ids' | 'phones' | 'emails' | ''

type NotificationPayload = {
    title: string
    body: string
    target_type: TargetType
    target_ids?: Array<string | number>
    action?: string
    send_at?: string
}

interface NotificationRow {
    id: number
    title: string
    body: string
    action?: string | null
    target_type: TargetType
    target_ids?: Array<string | number> | string | null
    send_at?: string | null
}

interface Props extends FormComponentProps {
    visible: boolean
    saving: boolean
    notification?: NotificationRow | null
    resending?: boolean
    onCancel: () => void
    onSubmit: (payload: NotificationPayload) => Promise<void>
}

interface State {
    targetType: TargetType
    targetsInitial: string
    savedTargets: Partial<Record<TargetType, string>>
    isOther: boolean
    isScheduled: boolean
    emojiPickerOpenFor: 'title' | 'body' | null
    step: number
    savedValues: Record<string, any>
}

const NOTIF_STEPS = [
    { label: 'תוכן', desc: 'כותרת והודעה' },
    { label: 'שליחה', desc: 'קהל ותזמון' }
]

const NOTIF_STEP_FIELDS = [
    ['title', 'body'],
    [] as string[]
]

const { Option } = Select
const { TextArea } = Input
const { CheckableTag } = Tag

const TARGET_OPTIONS: Array<{ label: string; value: TargetType }> = [
    { label: 'כל המשתמשים', value: 'all' },
    { label: 'טלפונים', value: 'phones' },
    { label: 'אימיילים', value: 'emails' },
    { label: 'מזהי משתמש', value: 'user_ids' },
    { label: 'קופות חולים', value: 'hmo' }
]

const HMO_OPTIONS = [
    { label: 'כללית', value: '1' },
    { label: 'מכבי', value: '2' },
    { label: 'מאוחדת', value: '3' },
    { label: 'לאומית', value: '4' },
    { label: 'משרד הביטחון', value: '5' }
]


class NotificationModal extends React.Component<Props, State> {
    state: State = {
        targetType: 'all',
        targetsInitial: '',
        savedTargets: {},
        isOther: false,
        isScheduled: false,
        emojiPickerOpenFor: null,
        step: 0,
        savedValues: {}
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleAutofillHotkey)
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleAutofillHotkey)
    }

    // test-only autofill, ceiling = fixed field list per step; extend NOTIF_STEP_FIELDS-style if more steps are added
    handleAutofillHotkey = (e: KeyboardEvent) => {
        if (process.env.NODE_ENV === 'production') return
        if (!this.props.visible) return
        if (!(e.ctrlKey || e.metaKey) || e.code !== 'KeyK') return
        e.preventDefault()

        const rand = (n: number) => Array.from({ length: n }, () => Math.floor(Math.random() * 10)).join('')
        const EMOJIS = ['😀', '🔥', '⭐', '🚀', '👩‍💻', '💻', '🖥️', '⌨️', '🖱️', '🤖', '⚙️', '💾']
        const emoji = () => EMOJIS[Math.floor(Math.random() * EMOJIS.length)]

        if (this.state.step === 0) {
            this.props.form.setFieldsValue({
                title: 'בדיקה ' + rand(5) + ' ' + emoji(),
                body: 'תוכן בדיקה ' + rand(6) + ' ' + emoji()
            })
            return
        }

        if (this.state.isOther) {
            const { targetType } = this.state
            if (targetType === 'hmo') {
                this.props.form.setFieldsValue({ hmos: ['1'] })
            } else if (targetType === 'phones') {
                this.props.form.setFieldsValue({ targets: '05' + Math.floor(10000000 + Math.random() * 89999999) })
            } else if (targetType === 'emails') {
                this.props.form.setFieldsValue({ targets: 'test' + rand(6) + '@example.com' })
            } else if (targetType === 'user_ids') {
                this.props.form.setFieldsValue({ targets: String(Math.floor(1 + Math.random() * 100000)) })
            } else {
                this.handleTargetTypeChange('phones')
                setTimeout(() => this.props.form.setFieldsValue({ targets: '05' + Math.floor(10000000 + Math.random() * 89999999) }))
            }
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.visible && !prevProps.visible) {
            const { notification } = this.props
            // destroyOnClose unmounts the form fields, so getFieldDecorator's initialValue
            // (sourced from savedValues via getSavedFieldValue) re-applies on next open —
            // seed it with the notification's values instead of wiping it, or resend shows blank fields
            this.setState({
                step: 0,
                savedValues: notification
                    ? { title: notification.title || '', body: notification.body || '', action: notification.action || '' }
                    : {}
            })
            if (notification) {
                // populate form for editing
                const targetType = notification.target_type || 'all'
                let targetsStr = ''
                if (notification.target_ids) {
                    const ids = Array.isArray(notification.target_ids)
                        ? notification.target_ids
                        : (() => {
                              try {
                                  return JSON.parse(notification.target_ids as string)
                              } catch (e) {
                                  return []
                              }
                          })()
                    targetsStr = ids.join('\n')
                }

                const sendAt = notification.send_at ? moment(notification.send_at) : null
                const isScheduled = !!notification.send_at
                const initialTargets = targetType !== 'hmo' && targetType !== 'all' ? targetsStr : ''

                // destroyOnClose unmounts fields on close, so they aren't registered yet on this
                // render pass — defer setFieldsValue until after re-render/re-registration, same
                // as handleBack does, or rc-form's live value stays undefined (next-button check fails)
                setTimeout(() => {
                    this.props.form.setFieldsValue({
                        title: notification.title || '',
                        body: notification.body || '',
                        action: notification.action || '',
                        hmos:
                            targetType === 'hmo' && notification.target_ids
                                ? String(
                                      Array.isArray(notification.target_ids)
                                          ? notification.target_ids[0]
                                          : notification.target_ids
                                  )
                                : undefined
                    })

                    // getFieldDecorator initialValue only applies on first register, so on a
                    // second resend the stale targets field must be set explicitly
                    if (initialTargets) {
                        this.props.form.setFieldsValue({ targets: initialTargets })
                    }
                    if (isScheduled && sendAt) {
                        this.props.form.setFieldsValue({ send_date: sendAt, send_time: sendAt })
                    }
                })

                this.setState({
                    targetType,
                    isOther: targetType !== 'all',
                    isScheduled,
                    targetsInitial: initialTargets,
                    savedTargets: initialTargets ? { [targetType]: initialTargets } : {}
                })
            } else {
                this.setState({
                    targetType: 'all',
                    targetsInitial: '',
                    savedTargets: {},
                    isOther: false,
                    isScheduled: false,
                    emojiPickerOpenFor: null
                })
                this.props.form.resetFields()
            }
        }
    }

    // Shared wizard helpers — no more copy-paste across modals
    get wizard() {
        return makeWizardHandlers(
            this.props.form,
            NOTIF_STEP_FIELDS,
            () => this.state,
            updater => this.setState(updater)
        )
    }

    insertEmoji = (fieldName: 'title' | 'body', emoji: string) => {
        const currentValue = this.props.form.getFieldValue(fieldName) || ''
        this.props.form.setFieldsValue({
            [fieldName]: `${currentValue}${emoji}`
        })
    }

    handleTargetTypeChange = (newType: TargetType) => {
        makeTargetTypeHandler<TargetType>(
            this.props.form,
            () => ({ targetType: this.state.targetType, savedTargets: this.state.savedTargets }),
            updater => this.setState(updater),
            { hmos: [] }
        )(newType)
    }

    submitLock = false

    handleSubmit = () => {
        if (this.submitLock || this.props.saving) return
        const { form, onSubmit } = this.props
        const { targetType, savedValues } = this.state

        form.validateFields(async (err, values) => {
            if (err) return

            const merged = Object.assign({}, savedValues, values)
            const title = (merged.title || '').trim()
            const body = (merged.body || '').trim()
            if (!title || !body) return

            if (this.state.isOther && !targetType) return

            let targetIds: Array<string | number> | undefined
            if (targetType === 'hmo') {
                targetIds = Array.isArray(values.hmos) && values.hmos.length > 0 ? values.hmos : (values.hmos && !Array.isArray(values.hmos) ? [values.hmos] : [])
                if (targetIds.length === 0) {
                    form.setFields({
                        hmos: {
                            value: values.hmos,
                            errors: [new Error('בחר לפחות קופת חולים אחת')]
                        }
                    })
                    return
                }
            } else if (targetType !== 'all') {
                targetIds = parseTargets(values.targets || '')
                if (targetType === 'phones') {
                    targetIds = targetIds.map(v => (v.startsWith('0') ? v.slice(1) : v))
                }
                if (targetIds.length === 0) {
                    form.setFields({
                        targets: {
                            value: values.targets,
                            errors: [new Error('יש להזין לפחות יעד אחד')]
                        }
                    })
                    return
                }
            }

            let send_at: string | undefined

            if (this.state.isScheduled) {
                const sendDate = values.send_date
                const sendTime = values.send_time

                if (!sendDate || !sendTime) {
                    form.setFields({
                        send_date: {
                            value: sendDate,
                            errors: [new Error('יש לבחור גם תאריך וגם שעה')]
                        },
                        send_time: {
                            value: sendTime,
                            errors: [new Error('יש לבחור גם תאריך וגם שעה')]
                        }
                    })
                    return
                }

                const scheduled = sendDate
                    .clone()
                    .hour(sendTime.hour())
                    .minute(sendTime.minute())
                    .second(0)

                if (!scheduled.isAfter(moment())) {
                    form.setFields({
                        send_date: {
                            value: sendDate,
                            errors: [new Error('מועד השליחה חייב להיות בעתיד')]
                        }
                    })
                    return
                }

                send_at = scheduled.format('YYYY-MM-DD HH:mm:ss')
            }

            const payload: NotificationPayload = {
                title,
                body,
                target_type: targetType
            }

            if (targetIds && targetType !== 'all') {
                payload.target_ids = targetIds
            }

            const action = merged.action || ''
            if (action) {
                payload.action = action
            }

            if (send_at) {
                payload.send_at = send_at
            }

            this.submitLock = true
            try {
                await onSubmit(payload)
            } finally {
                this.submitLock = false
            }
        })
    }

    renderTargetFields() {
        const { form: { getFieldDecorator } } = this.props
        const { targetType, targetsInitial } = this.state

        if (targetType === 'all' || !targetType) return null

        if (targetType === 'hmo') {
            return (
                <Form.Item label="קופות חולים" style={{ marginBottom: 15, paddingBottom: 0 }}>
                    {getFieldDecorator('hmos', { initialValue: [] })(
                        <Select placeholder="בחר קופות חולים" style={{ width: '100%' }}
                            className="commission-select mediwho-notification-hmo-select" dropdownClassName="commission-dropdown">
                            {HMO_OPTIONS.map(option => (
                                <Option key={option.value} value={option.value}>{option.label}</Option>
                            ))}
                        </Select>
                    )}
                </Form.Item>
            )
        }

        return <TargetChipField form={this.props.form} targetType={targetType} targetsInitial={targetsInitial} className="mediwho-notification-target-chip-input" />
    }

    renderStep0() {
        const { form: { getFieldDecorator } } = this.props
        const getSavedFieldValue = this.wizard.getSavedFieldValue
        return (
            <div className="mediwho-card">
                <SectionTitle label="תוכן ההתראה" color="#1890ff" />
                <Form.Item
                    className="mediwho-app-emoji-field"
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                            <span>כותרת</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <NotificationEmojiPicker
                                    visible={this.state.emojiPickerOpenFor === 'title'}
                                    onVisibleChange={v => this.setState({ emojiPickerOpenFor: v ? 'title' : null })}
                                    onSelectEmoji={emoji => this.insertEmoji('title', emoji)}
                                />
                            </span>
                        </span>
                    }
                    style={{ marginBottom: 12 }}
                >
                    {getFieldDecorator('title', {
                        initialValue: getSavedFieldValue('title', ''),
                        rules: [{ required: true, message: 'כותרת היא שדה חובה' }]
                        })(<Input placeholder="הכנס כותרת" className="mediwho-notification-title-input mediwho-notification-text-input" />)}
                </Form.Item>

                <Form.Item
                    className="mediwho-app-emoji-field"
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                            <span>תוכן</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <NotificationEmojiPicker
                                    visible={this.state.emojiPickerOpenFor === 'body'}
                                    onVisibleChange={v => this.setState({ emojiPickerOpenFor: v ? 'body' : null })}
                                    onSelectEmoji={emoji => this.insertEmoji('body', emoji)}
                                />
                            </span>
                        </span>
                    }
                    style={{ marginBottom: 12 }}
                >
                    {getFieldDecorator('body', {
                        initialValue: getSavedFieldValue('body', ''),
                        rules: [{ required: true, message: 'תוכן הוא שדה חובה' }]
                    })(
                        <TextArea
                            rows={4}
                            placeholder="הודעה למשתמשים"
                            className="mediwho-notification-body-input mediwho-notification-textarea"
                            style={{ direction: 'rtl', textAlign: 'right' }}
                        />
                    )}
                </Form.Item>
            </div>
        )
    }

    renderStep1() {
        const { form: { getFieldDecorator } } = this.props
        const { targetType, isOther, isScheduled } = this.state
        const getSavedFieldValue = this.wizard.getSavedFieldValue
        return (
            <div>
                <div className="mediwho-card">
                    <SectionTitle label="קהל יעד" color="#13c2c2" />
                    <Form.Item style={{ marginBottom: isOther ? 8 : 12 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: isOther ? 10 : 0 }}>
                            <CheckableTag
                                className="mediwho-notification-target-all-tag"
                                checked={!isOther}
                                onChange={checked => {
                                    if (checked) {
                                        this.setState({ isOther: false })
                                        this.handleTargetTypeChange('all')
                                    }
                                }}
                                style={{ padding: '6px 14px', borderRadius: 999, border: '1px solid #d9d9d9', marginRight: 0, fontWeight: 500 }}
                            >
                                כל המשתמשים
                            </CheckableTag>
                            <CheckableTag
                                className="notif-target-other-tag mediwho-notification-target-specific-tag"
                                checked={isOther}
                                onChange={checked => {
                                    if (checked) {
                                        this.setState({ isOther: true })
                                        this.handleTargetTypeChange('')
                                    }
                                }}
                                style={{ padding: '6px 14px', borderRadius: 999, border: '1px solid #d9d9d9', marginRight: 0, fontWeight: 500 }}
                            >
                                ספציפי
                            </CheckableTag>
                        </div>
                        {isOther && (
                            <Select
                                value={targetType || undefined}
                                placeholder="בחר סוג יעד"
                                onChange={(val: TargetType) => this.handleTargetTypeChange(val)}
                                style={{ width: '100%' }}
                                className="commission-select notif-target-type-select mediwho-notification-target-type-select"
                                dropdownClassName="commission-dropdown notif-target-type-dropdown"
                            >
                                {TARGET_OPTIONS.filter(o => o.value !== 'all').map(option => (
                                    <Option key={option.value} value={option.value}>{option.label}</Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                    {this.renderTargetFields()}
                </div>

                <div className="mediwho-card mediwho-card-mb8">
                    <SectionTitle label="פעולה ותזמון" color="#722ed1" />
                    <Form.Item label="פעולה" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('action', {
                            initialValue: getSavedFieldValue('action', this.props.notification ? this.props.notification.action || '' : '')
                        })(
                                <Select
                                    placeholder="בחר action"
                                    style={{ width: '100%' }}
                                    className="commission-select mediwho-notification-action-select"
                                    dropdownClassName="commission-dropdown"
                                >
                                {ACTION_OPTIONS.map(option => (
                                    <Option key={option.value || 'none'} value={option.value}>{option.label}</Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>

                    <Form.Item label="מועד שליחה" style={{ marginBottom: isScheduled ? 8 : 12 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <CheckableTag
                                className="notif-schedule-now-tag mediwho-notification-schedule-now-tag"
                                checked={!isScheduled}
                                onChange={checked => {
                                    if (checked) {
                                        this.setState({ isScheduled: false })
                                        this.props.form.setFieldsValue({ send_date: null, send_time: null })
                                    }
                                }}
                                style={{ padding: '6px 14px', borderRadius: 999, border: '1px solid #d9d9d9', marginRight: 0, fontWeight: 500 }}
                            >
                                עכשיו
                            </CheckableTag>
                            <CheckableTag
                                className="notif-schedule-other-tag mediwho-notification-schedule-other-tag"
                                checked={isScheduled}
                                onChange={checked => { if (checked) this.setState({ isScheduled: true }) }}
                                style={{ padding: '6px 14px', borderRadius: 999, border: '1px solid #d9d9d9', marginRight: 0, fontWeight: 500 }}
                            >
                                מתוזמן
                            </CheckableTag>
                        </div>
                    </Form.Item>

                    {isScheduled && (
                        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                            <Form.Item label="תאריך שליחה" style={{ flex: 1, marginBottom: 0 }}>
                                {getFieldDecorator('send_date')(
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        placeholder="בחר תאריך"
                                        className="mediwho-notification-send-date-input"
                                        disabledDate={current => !!current && current < moment().startOf('day')}
                                    />
                                )}
                            </Form.Item>
                            <Form.Item label="שעת שליחה" style={{ flex: 1, marginBottom: 0, position: 'relative' }}>
                                {getFieldDecorator('send_time')(
                                    <TimePicker
                                        style={{ width: '100%' }}
                                        placeholder="בחר שעה"
                                        format="HH:mm"
                                        className="mediwho-notification-send-time-input"
                                        popupClassName="mediwho-app-time-popup"
                                        getPopupContainer={(trigger: any) => trigger.parentNode}
                                    />
                                )}
                            </Form.Item>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    render() {
        const { visible, onCancel, saving } = this.props
        const { step } = this.state

        const { notification, resending } = this.props
        const isEdit = !!(notification && notification.id)
        const { targetType, isOther, targetsInitial } = this.state

        let targetIncomplete = false
        if (isOther) {
            if (!targetType) {
                targetIncomplete = true
            } else if (targetType === 'hmo') {
                const hmos = this.props.form.getFieldValue('hmos')
                targetIncomplete = !Array.isArray(hmos) || hmos.length === 0
            } else {
                // getFieldValue('targets') can still be stale on the same render the field
                // decorator first registers (e.g. right after switching to step 2 on resend),
                // so fall back to the initial value we already know from state
                const targets = this.props.form.getFieldValue('targets')
                const effectiveTargets = targets === undefined ? targetsInitial : targets
                targetIncomplete = parseTargets(effectiveTargets || '').length === 0
            }
        }

        const { handleNext, handleBack } = this.wizard
        const footer = renderWizardFooter({
            step,
            lastStep: 1,
            saving,
            form: this.props.form,
            requiredFieldsByStep: NOTIF_STEP_FIELDS,
            extraDisabled: step === 1 && targetIncomplete,
            onNext: handleNext,
            onBack: handleBack,
            onSave: this.handleSubmit,
            onCancel,
            saveLabel: isEdit ? 'שמירה' : 'שליחה'
        })

        return (
            <Modal
                visible={visible}
                title={resending ? 'שליחה חוזרת' : isEdit ? 'עריכת התראה' : 'התראה חדשה'}
                onCancel={onCancel}
                centered
                width={630}
                wrapClassName="mediwho-app-modal-wrap"
                bodyStyle={{ padding: '24px 24px 8px' }}
                footer={footer}
                destroyOnClose
            >
                <style>{COMMISSION_SELECT_STYLES}</style>
                <Form layout="vertical" className="mediwho-app-modal">
                    <StepBar current={step} steps={NOTIF_STEPS} />
                    {step === 0 ? this.renderStep0() : this.renderStep1()}
                </Form>
            </Modal>
        )
    }
}

export default Form.create<Props>()(NotificationModal)
